import React from 'react';
import { TaxStatus } from '../types';
import { STATUS_STEPS } from '../constants';

interface StatusTrackerProps {
  currentStatus: TaxStatus;
}

const StatusTracker: React.FC<StatusTrackerProps> = ({ currentStatus }) => {
  const currentIndex = STATUS_STEPS.indexOf(currentStatus);

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6 overflow-hidden">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-lg font-semibold text-slate-800">Tax Return Status</h3>
        <span className="px-3 py-1 bg-brand-50 text-brand-700 text-sm font-bold rounded-full border border-brand-100">
          {currentStatus}
        </span>
      </div>

      <div className="relative px-2">
        {/* Background Line */}
        <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 rounded-full" />
        
        {/* Active Progress Line */}
        <div 
            className="absolute top-1/2 left-0 h-1 bg-brand-500 -translate-y-1/2 rounded-full transition-all duration-1000 ease-in-out" 
            style={{ width: `${(currentIndex / (STATUS_STEPS.length - 1)) * 100}%` }}
        />
        
        {/* Dots and Labels */}
        <div className="relative flex justify-between w-full">
          {STATUS_STEPS.map((step, index) => {
            const isCompleted = index <= currentIndex;
            const isCurrent = index === currentIndex;
            const isFirst = index === 0;
            const isLast = index === STATUS_STEPS.length - 1;

            return (
              <div key={step} className="flex flex-col items-center group relative">
                {/* Dot */}
                <div 
                  className={`w-4 h-4 rounded-full border-2 z-10 transition-colors duration-300 ${
                    isCompleted 
                      ? 'bg-brand-500 border-brand-500' 
                      : 'bg-white border-slate-300'
                  } ${isCurrent ? 'ring-4 ring-brand-100' : ''}`}
                />
                
                {/* Label */}
                <div 
                    className={`absolute top-6 text-xs font-medium w-32 transition-colors duration-300 hidden md:block ${
                        isCurrent ? 'text-brand-700 font-bold' : isCompleted ? 'text-slate-600' : 'text-slate-400'
                    } ${
                        // Dynamic Alignment to prevent overflow
                        isFirst ? 'text-left left-0 translate-x-0' : 
                        isLast ? 'text-right right-0 translate-x-0' : 
                        'text-center left-1/2 -translate-x-1/2'
                    }`}
                >
                  {step}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Spacer to accommodate the absolute positioned labels which might sit outside normal flow slightly if we didn't add margin, 
          but here we used margin-bottom in parent. Adding a small spacer div for safety on mobile view if labels were visible. */}
      <div className="h-4 md:h-6"></div>

      {/* Mobile-only current step label */}
      <p className="text-center text-sm text-slate-500 md:hidden mt-2">
         Current Stage: <span className="font-semibold text-brand-600">{currentStatus}</span>
      </p>
    </div>
  );
};

export default StatusTracker;
